import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { FileText, TrendingUp, MessageCircle, Clock, CheckCircle2, XCircle, Eye, Users, ThumbsUp, ThumbsDown, Trees } from "lucide-react";

const surveyMetrics = [
  { label: "Total Responses", value: "1,247", change: "+89 this week", icon: Users },
  { label: "Active Surveys", value: "3", change: "1 closing soon", icon: FileText },
  { label: "Response Rate", value: "82.1%", change: "+6.3% vs target", icon: TrendingUp },
  { label: "Public Meetings", value: "8", change: "3 completed", icon: MessageCircle }
];

const responseData = [
  { date: "2024-08-01", responses: 45 },
  { date: "2024-08-02", responses: 67 },
  { date: "2024-08-03", responses: 52 },
  { date: "2024-08-04", responses: 89 },
  { date: "2024-08-05", responses: 78 },
  { date: "2024-08-06", responses: 94 },
  { date: "2024-08-07", responses: 112 }
];

const priorityData = [
  { priority: "New Toilets", support: 89.2, count: 1112, color: "#22c55e" },
  { priority: "Better Lighting", support: 87.5, count: 1091, color: "#3b82f6" },
  { priority: "More Parking", support: 78.3, count: 976, color: "#f59e0b" },
  { priority: "Playground Upgrade", support: 74.6, count: 930, color: "#8b5cf6" },
  { priority: "Outdoor Cinema", support: 65.2, count: 813, color: "#06b6d4" },
  { priority: "Bike Facilities", support: 58.7, count: 732, color: "#10b981" }
];

const communityNeeds = [
  { need: "Better accessibility", mentions: 456, sentiment: "positive" },
  { need: "More family facilities", mentions: 389, sentiment: "positive" },
  { need: "Safety improvements", mentions: 334, sentiment: "positive" },
  { need: "Environmental protection", mentions: 298, sentiment: "positive" },
  { need: "Traffic concerns", mentions: 267, sentiment: "negative" },
  { need: "Parking availability", mentions: 234, sentiment: "mixed" }
];

const activeSurveys = [
  {
    id: 1,
    title: "Park Improvement Priorities",
    status: "active",
    responses: 1247,
    startDate: "2024-05-01",
    endDate: "2024-09-30",
    description: "Help us prioritize which park improvements are most important to you"
  },
  {
    id: 2,
    title: "Playground Design Preferences",
    status: "active",
    responses: 567,
    startDate: "2024-07-15",
    endDate: "2024-09-15",
    description: "Share your input on playground equipment and design features"
  },
  {
    id: 3,
    title: "Evening Usage & Safety",
    status: "closing-soon",
    responses: 289,
    startDate: "2024-08-01",
    endDate: "2024-08-31",
    description: "Tell us about your evening park usage and safety concerns"
  }
];

const closedSurveys = [
  {
    id: 4,
    title: "Current Park Usage Study",
    status: "closed",
    responses: 1834,
    target: 1500,
    endDate: "2024-07-31",
    description: "Understanding how the community currently uses the park"
  },
  {
    id: 5,
    title: "Initial Needs Assessment",
    status: "closed", 
    responses: 1267,
    target: 1000,
    endDate: "2024-06-30",
    description: "First survey to identify community needs and priorities"
  }
];

// Mock word cloud data focused on park features
const wordCloudWords = [
  { text: "family-friendly", size: 64, color: "#22c55e" },
  { text: "safe", size: 58, color: "#3b82f6" },
  { text: "accessible", size: 52, color: "#f59e0b" },
  { text: "playground", size: 48, color: "#8b5cf6" },
  { text: "toilets", size: 45, color: "#ef4444" },
  { text: "lighting", size: 42, color: "#06b6d4" },
  { text: "parking", size: 38, color: "#10b981" },
  { text: "environment", size: 35, color: "#84cc16" },
  { text: "community", size: 32, color: "#ec4899" },
  { text: "recreation", size: 29, color: "#f97316" }
];

const getStatusIcon = (status) => {
  switch (status) {
    case "active":
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case "closing-soon":
      return <Clock className="h-4 w-4 text-yellow-600" />;
    case "closed":
      return <XCircle className="h-4 w-4 text-gray-600" />;
    default:
      return <FileText className="h-4 w-4 text-gray-600" />;
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800 hover:bg-green-100";
    case "closing-soon":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
    case "closed":
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
};

export function SurveyTab() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Community Input</h1>
          <p className="text-muted-foreground mt-1">
            Community feedback, survey responses, and public consultation insights
          </p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Trees className="h-3 w-3" />
          Live Feedback
        </Badge>
      </div>

      {/* Survey Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {surveyMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index}>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {metric.label}
                </CardDescription>
                <CardTitle className="text-2xl">{metric.value}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{metric.change}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Response Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Daily Response Trends</CardTitle>
            <CardDescription>Survey responses received per day over the past week</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={responseData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                <YAxis />
                <Line 
                  type="monotone" 
                  dataKey="responses" 
                  stroke="hsl(var(--chart-1))" 
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Community Priorities */}
        <Card>
          <CardHeader>
            <CardTitle>Community Priorities</CardTitle>
            <CardDescription>Top park improvements ranked by community support</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {priorityData.map((priority, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">{priority.priority}</span>
                    <span className="text-sm text-muted-foreground">
                      {priority.support}% ({priority.count} responses)
                    </span>
                  </div>
                  <Progress value={priority.support} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Community Feedback Word Cloud */}
      <Card>
        <CardHeader>
          <CardTitle>Community Feedback Themes</CardTitle>
          <CardDescription>Most frequently mentioned topics in community responses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-center gap-4 p-8 min-h-[200px] bg-muted/30 rounded-lg">
            {wordCloudWords.map((word, index) => (
              <span
                key={index}
                style={{ 
                  fontSize: `${word.size}px`, 
                  color: word.color,
                  fontWeight: 'bold',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                }}
                className="cursor-pointer hover:opacity-80 transition-opacity select-none"
              >
                {word.text}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Community Needs */}
      <Card>
        <CardHeader>
          <CardTitle>Key Community Needs</CardTitle>
          <CardDescription>Most frequently mentioned needs and concerns from community feedback</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {communityNeeds.map((need, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {need.sentiment === "positive" && <ThumbsUp className="h-4 w-4 text-green-600" />}
                    {need.sentiment === "negative" && <ThumbsDown className="h-4 w-4 text-red-600" />}
                    {need.sentiment === "mixed" && <MessageCircle className="h-4 w-4 text-yellow-600" />}
                    <span className="font-medium">{need.need}</span>
                  </div>
                  <Badge 
                    variant="secondary" 
                    className={
                      need.sentiment === "positive" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                      need.sentiment === "negative" ? "bg-red-100 text-red-800 hover:bg-red-100" :
                      "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                    }
                  >
                    {need.sentiment}
                  </Badge>
                </div>
                <span className="font-medium">{need.mentions} mentions</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Active Surveys */}
      <Card>
        <CardHeader>
          <CardTitle>Active Surveys</CardTitle>
          <CardDescription>Current surveys you can participate in</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {activeSurveys.map((survey) => {
              // Calculate time-based progress
              const startTime = new Date(survey.startDate).getTime();
              const endTime = new Date(survey.endDate).getTime();
              const currentTime = new Date().getTime();
              const totalDuration = endTime - startTime;
              const elapsed = currentTime - startTime;
              const timeProgress = Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));
              const daysRemaining = Math.ceil((endTime - currentTime) / (1000 * 60 * 60 * 24));
              
              return (
                <div key={survey.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(survey.status)}
                      <h3 className="font-medium">{survey.title}</h3>
                      <Badge 
                        variant="secondary" 
                        className={getStatusBadgeColor(survey.status)}
                      >
                        {survey.status.replace("-", " ")}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        Participate
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{survey.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{survey.responses} responses collected</span>
                      <span>{daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Closing soon'}</span>
                    </div>
                    <Progress value={timeProgress} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Started: {new Date(survey.startDate).toLocaleDateString()}</span>
                      <span>Closes: {new Date(survey.endDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Closed Surveys */}
      <Card>
        <CardHeader>
          <CardTitle>Completed Surveys</CardTitle>
          <CardDescription>Previous surveys and their results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {closedSurveys.map((survey) => (
              <div key={survey.id} className="p-4 border rounded-lg space-y-3 opacity-75">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(survey.status)}
                    <h3 className="font-medium">{survey.title}</h3>
                    <Badge 
                      variant="secondary" 
                      className={getStatusBadgeColor(survey.status)}
                    >
                      {survey.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                      View Results
                    </Button>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground">{survey.description}</p>
                
                <div className="flex justify-between text-sm">
                  <span>Final: {survey.responses} / {survey.target} responses</span>
                  <span>{Math.round((survey.responses / survey.target) * 100)}% participation</span>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  Completed: {new Date(survey.endDate).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}